import React, { useState } from "react";
import { post } from "../../../services/smartApiService";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

interface FetchPricesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  symbol: string;
}

interface FetchPricesFormData {
  symbol: string;
  exchange: string;
  timeframe: string;
  from_date: string;
  to_date: string;
}

const schema = Yup.object({
  symbol: Yup.string().required("Symbol is required"),
  exchange: Yup.string().required("Exchange is required"),
  timeframe: Yup.string().required("Timeframe is required"),
  from_date: Yup.string().required("From date is required"),
  to_date: Yup.string().required("To date is required"),
});

const FetchPricesModal: React.FC<FetchPricesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  symbol,
}) => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FetchPricesFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      symbol: symbol,
      exchange: "NSE",
      timeframe: "1d",
      from_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      to_date: new Date().toISOString().split("T")[0],
    },
  });

  const onSubmit = (data: FetchPricesFormData) => {
    setLoading(true);

    // Format dates to ISO format with time
    const fromDateTime = `${data.from_date}T00:00:00`;
    const toDateTime = `${data.to_date}T23:59:59`;

    const params = new URLSearchParams({
      symbol: data.symbol,
      exchange: data.exchange,
      timeframe: data.timeframe,
      from_date: fromDateTime,
      to_date: toDateTime,
    });

    post(`/api/dashboard/prices/fetch-historical?${params.toString()}`, {})
      .subscribe({
        next: (response: any) => {
          console.log("Prices fetched successfully:", response);
          setLoading(false);
          reset();
          onSuccess();
        },
        error: (err: any) => {
          console.error("Failed to fetch prices:", err);
          setLoading(false);
        },
      });
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`modal ${isOpen ? "is-active" : ""}`}>
      <div className="modal-background" onClick={handleClose}></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">
            <span className="icon-text">
              <span className="icon has-text-info">
                <i className="fas fa-download"></i>
              </span>
              <span>Fetch Historical Prices</span>
            </span>
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
          ></button>
        </header>

        <section className="modal-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="field">
              <label className="label">Symbol</label>
              <div className="control">
                <Controller
                  name="symbol"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className={`input ${errors.symbol ? "is-danger" : ""}`}
                      type="text"
                      placeholder="e.g., TCS"
                    />
                  )}
                />
              </div>
              {errors.symbol && (
                <p className="help is-danger">{errors.symbol.message}</p>
              )}
            </div>

            <div className="field">
              <label className="label">Exchange</label>
              <div className="control">
                <Controller
                  name="exchange"
                  control={control}
                  render={({ field }) => (
                    <div className="select is-fullwidth">
                      <select {...field}>
                        <option value="NSE">NSE</option>
                        <option value="BSE">BSE</option>
                      </select>
                    </div>
                  )}
                />
              </div>
              {errors.exchange && (
                <p className="help is-danger">{errors.exchange.message}</p>
              )}
            </div>

            <div className="field">
              <label className="label">Timeframe</label>
              <div className="control">
                <Controller
                  name="timeframe"
                  control={control}
                  render={({ field }) => (
                    <div className="select is-fullwidth">
                      <select {...field}>
                        <option value="1m">1 Minute</option>
                        <option value="5m">5 Minutes</option>
                        <option value="15m">15 Minutes</option>
                        <option value="30m">30 Minutes</option>
                        <option value="1h">1 Hour</option>
                        <option value="1d">1 Day</option>
                      </select>
                    </div>
                  )}
                />
              </div>
              {errors.timeframe && (
                <p className="help is-danger">{errors.timeframe.message}</p>
              )}
            </div>

            <div className="columns">
              <div className="column">
                <div className="field">
                  <label className="label">From Date</label>
                  <div className="control">
                    <Controller
                      name="from_date"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          className={`input ${
                            errors.from_date ? "is-danger" : ""
                          }`}
                          type="date"
                        />
                      )}
                    />
                  </div>
                  {errors.from_date && (
                    <p className="help is-danger">{errors.from_date.message}</p>
                  )}
                </div>
              </div>

              <div className="column">
                <div className="field">
                  <label className="label">To Date</label>
                  <div className="control">
                    <Controller
                      name="to_date"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          className={`input ${errors.to_date ? "is-danger" : ""}`}
                          type="date"
                        />
                      )}
                    />
                  </div>
                  {errors.to_date && (
                    <p className="help is-danger">{errors.to_date.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="notification is-info is-light mt-4">
              <p className="is-size-7">
                <span className="icon-text">
                  <span className="icon">
                    <i className="fas fa-info-circle"></i>
                  </span>
                  <span>
                    This will fetch historical price data for the selected period
                    and timeframe. Analytics will be automatically recalculated.
                  </span>
                </span>
              </p>
            </div>
          </form>
        </section>

        <footer className="modal-card-foot">
          <button
            className={`button is-success ${loading ? "is-loading" : ""}`}
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
          >
            <span className="icon">
              <i className="fas fa-check"></i>
            </span>
            <span>Fetch Prices</span>
          </button>
          <button
            className="button"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
};

export default FetchPricesModal;
